import React, { useEffect, useState, useRef } from 'react';
import { FinancialSummary } from '../components/player/FinancialSummary';
import { Assets } from '../components/player/Assets';
import { Liabilities } from '../components/player/Liabilities';
import { Contracts } from '../components/player/Contracts';
import { Income } from '../components/player/Income';
import { Expenses } from '../components/player/Expenses';
import { Edit2, Check, Save } from 'lucide-react';
export function Cashflow() {
  const [playerName, setPlayerName] = useState('Player');
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Financial data state
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalLiabilities, setTotalLiabilities] = useState(0);
  const [totalAssetCashflow, setTotalAssetCashflow] = useState(0);
  const [totalLiabilityPayments, setTotalLiabilityPayments] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalCash, setTotalCash] = useState(5000);
  // Refs to access component data
  const assetsRef = useRef(null);
  const liabilitiesRef = useRef(null);
  const contractsRef = useRef(null);
  const incomeRef = useRef(null);
  const expensesRef = useRef(null);
  // Load data from localStorage on initial render
  useEffect(() => {
    const savedData = localStorage.getItem('playerData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setPlayerName(parsedData.playerName || 'Player');
        setTotalAssets(parsedData.totalAssets || 0);
        setTotalLiabilities(parsedData.totalLiabilities || 0);
        setTotalAssetCashflow(parsedData.totalAssetCashflow || 0);
        setTotalLiabilityPayments(parsedData.totalLiabilityPayments || 0);
        setTotalIncome(parsedData.totalIncome || 0);
        setTotalExpenses(parsedData.totalExpenses || 0);
        setTotalCash(parsedData.totalCash || 5000);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(e.target.value);
  };
  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
    }
  };
  const handleAssetChange = (assetValue: number, cashflow: number) => {
    setTotalAssets(assetValue);
    setTotalAssetCashflow(cashflow);
  };
  const handleLiabilitiesChange = (liabilityValue: number) => {
    setTotalLiabilities(liabilityValue);
  };
  const handleLiabilityPaymentsChange = (payments: number) => {
    setTotalLiabilityPayments(payments);
  };
  const handleIncomeChange = (income: number) => {
    setTotalIncome(income);
  };
  const handleExpensesChange = (expenses: number) => {
    setTotalExpenses(expenses);
  };
  const handleCashChange = (cash: number) => {
    setTotalCash(cash);
  };
  const handleSave = () => {
    // Get data from component refs if available
    // These will be undefined if the components don't expose their data via refs
    const assets = assetsRef.current?.getAssets ? assetsRef.current.getAssets() : undefined;
    const liabilities = liabilitiesRef.current?.getLiabilities ? liabilitiesRef.current.getLiabilities() : undefined;
    const contracts = contractsRef.current?.getContracts ? contractsRef.current.getContracts() : undefined;
    const incomeDetails = incomeRef.current?.getIncomeDetails ? incomeRef.current.getIncomeDetails() : undefined;
    const expenseDetails = expensesRef.current?.getExpenseDetails ? expensesRef.current.getExpenseDetails() : undefined;
    const dataToSave = {
      playerName,
      totalAssets,
      totalLiabilities,
      totalAssetCashflow,
      totalLiabilityPayments,
      totalIncome,
      totalExpenses,
      totalCash,
      lastSaved: new Date().toISOString(),
      // Include detailed data if available
      assets,
      liabilities,
      contracts,
      incomeDetails,
      expenseDetails
    };
    localStorage.setItem('playerData', JSON.stringify(dataToSave));
    // Show save confirmation message
    setSaveMessage('Data saved successfully!');
    // Clear the message after 3 seconds
    setTimeout(() => {
      setSaveMessage('');
    }, 3000);
    // Dispatch custom event to notify other components of the update
    const updateEvent = new CustomEvent('playerUpdated', {
      bubbles: true,
      detail: {
        timestamp: new Date().getTime()
      }
    });
    window.dispatchEvent(updateEvent);
    // Also trigger a storage event manually for same-tab updates
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'playerData',
      newValue: JSON.stringify(dataToSave),
      storageArea: localStorage
    }));
  };
  return <div className="p-8 max-w-6xl mx-auto" data-id="element-209">
      <div className="flex items-center mb-6" data-id="element-210">
        <div className="flex items-center" data-id="element-211">
          {isEditing ? <div className="flex items-center" data-id="element-212">
              <input type="text" value={playerName} onChange={handleNameChange} onKeyDown={handleKeyDown} className="text-3xl font-bold text-gray-800 border-b-2 border-green-500 mr-2 focus:outline-none" autoFocus data-id="element-213" />
              <button onClick={toggleEdit} className="text-green-500" data-id="element-214">
                <Check size={20} data-id="element-215" />
              </button>
            </div> : <div className="flex items-center" data-id="element-216">
              <h1 className="text-3xl font-bold text-gray-800 mr-2" data-id="element-217">
                {playerName}
              </h1>
              <button onClick={toggleEdit} className="text-gray-500 hover:text-green-500" data-id="element-218">
                <Edit2 size={16} data-id="element-219" />
              </button>
            </div>}
        </div>
      </div>
      <div className="space-y-6" data-id="element-220">
        {/* Financial Summary - 100% width */}
        <FinancialSummary totalAssets={totalAssets} totalLiabilities={totalLiabilities} monthlyIncome={totalIncome} monthlyExpenses={totalExpenses} initialCash={totalCash} onCashChange={handleCashChange} data-id="element-221" />
        {/* Assets and Liabilities - 50% width each */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-222">
          <Assets ref={assetsRef} onCashflowChange={cashflow => {}} onValueChange={(value, cashflow) => handleAssetChange(value, cashflow)} data-id="element-223" />
          <Liabilities ref={liabilitiesRef} onValueChange={handleLiabilitiesChange} onPaymentsChange={handleLiabilityPaymentsChange} data-id="element-224" />
        </div>
        {/* Contracts - 100% width */}
        <Contracts ref={contractsRef} data-id="element-225" />
        {/* Income and Expenses - 50% width each */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-226">
          <Income ref={incomeRef} totalAssetCashflow={totalAssetCashflow} onIncomeChange={handleIncomeChange} data-id="element-227" />
          <Expenses ref={expensesRef} liabilityPayments={totalLiabilityPayments} onExpensesChange={handleExpensesChange} data-id="element-228" />
        </div>
        {/* Save Button - Now at the bottom */}
        <div className="flex justify-center mt-8" data-id="element-229">
          <div className="flex items-center" data-id="element-230">
            {saveMessage && <span className="text-green-600 mr-3 font-medium" data-id="element-231">
                {saveMessage}
              </span>}
            <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors" data-id="element-232">
              <Save size={20} data-id="element-233" />
              Save All Data
            </button>
          </div>
        </div>
      </div>
    </div>;
}